import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import type { AbsenceType, MakeupStatus } from '@/lib/types';
import { ABSENCE_TYPES } from '@/lib/constants';

type AbsenceModalProps = {
  open: boolean;
  studentName: string;
  initialType: AbsenceType | null;
  initialNote: string | null;
  initialMakeupDate: string | null;
  initialMakeupTime: string | null;
  initialMakeupStatus: MakeupStatus | null;
  onClose: () => void;
  onSave: (
    type: AbsenceType,
    note: string,
    makeupDate: string,
    makeupTime: string,
    makeupStatus: 'pending' | 'not_needed'
  ) => void;
};

export default function AbsenceModal({
  open,
  studentName,
  initialType,
  initialNote,
  initialMakeupDate,
  initialMakeupTime,
  initialMakeupStatus,
  onClose,
  onSave,
}: AbsenceModalProps) {
  const [type, setType] = useState<AbsenceType | null>(initialType);
  const [note, setNote] = useState(initialNote || '');
  const [makeupDate, setMakeupDate] = useState(initialMakeupDate || '');
  const [makeupTime, setMakeupTime] = useState(initialMakeupTime || '');
  const [noMakeup, setNoMakeup] = useState(initialMakeupStatus === 'not_needed');

  useEffect(() => {
    if (open) {
      setType(initialType);
      setNote(initialNote || '');
      setMakeupDate(initialMakeupDate || '');
      setMakeupTime(initialMakeupTime || '');
      setNoMakeup(initialMakeupStatus === 'not_needed');
    }
  }, [open, initialType, initialNote, initialMakeupDate, initialMakeupTime, initialMakeupStatus]);

  const canSave = !!type && (type !== '기타' || note.trim().length > 0);

  function handleSave() {
    if (!type) return;
    if (type === '기타' && !note.trim()) return;
    const trimmedNote = type === '기타' ? note.trim() : '';
    if (noMakeup) {
      onSave(type, trimmedNote, '', '', 'not_needed');
    } else {
      onSave(type, trimmedNote, makeupDate, makeupTime, 'pending');
    }
  }

  const inputClass =
    'rounded-md border border-background-300 bg-white px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200';

  return (
    <Modal open={open} title={`${studentName} 학생 결석`} onClose={onClose}>
      <p className="text-sm text-foreground-500">결석 사유와 보충 여부를 입력하세요.</p>

      <div className="mt-4">
        <label className="mb-2 block text-sm font-medium text-foreground-700">결석 사유</label>
        <div className="flex flex-wrap gap-2">
          {ABSENCE_TYPES.map((t) => {
            const active = type === t;
            return (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-foreground-600 text-background-50'
                    : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>

        {type === '기타' && (
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-foreground-500">
              사유 직접 입력 <span className="text-accent-600">*</span>
            </label>
            <input
              type="text"
              value={note}
              maxLength={100}
              onChange={(e) => setNote(e.target.value)}
              placeholder="예) 코로나 격리, 가정 사정 등"
              className={`${inputClass} w-full`}
            />
          </div>
        )}
      </div>

      <div className="mt-5">
        <label className="mb-2 block text-sm font-medium text-foreground-700">보충 여부</label>
        <div className="flex gap-2">
          <button
            onClick={() => setNoMakeup(false)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              !noMakeup
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-300 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className="ri-refresh-line text-base"></i>
            보충 필요
          </button>
          <button
            onClick={() => setNoMakeup(true)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              noMakeup
                ? 'bg-secondary-500 text-background-50'
                : 'border border-background-300 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className="ri-calendar-close-line text-base"></i>
            보충 불필요
          </button>
        </div>
      </div>

      {noMakeup ? (
        <p className="mt-4 rounded-md bg-secondary-50 px-3 py-2 text-xs leading-relaxed text-secondary-800">
          보충이 필요 없는 결석으로 처리돼요. 결석보충달력의
          <strong className="font-semibold"> ‘보충 잡지 않은 결석’</strong> 목록에 나타나지 않아요.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground-500">보충 날짜</label>
              <input
                type="date"
                value={makeupDate}
                onChange={(e) => setMakeupDate(e.target.value)}
                className={`${inputClass} w-full`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground-500">보충 시간</label>
              <input
                type="time"
                value={makeupTime}
                onChange={(e) => setMakeupTime(e.target.value)}
                className={`${inputClass} w-full`}
              />
            </div>
          </div>

          <p className="mt-3 rounded-md bg-background-50 px-3 py-2 text-xs leading-relaxed text-foreground-500">
            보충 날짜를 아직 정하지 못했다면 비워두고 저장해도 돼요. 나중에
            <strong className="font-semibold text-foreground-700"> ‘결석보충달력’</strong>의
            ‘보충 잡지 않은 결석’에서 다시 잡을 수 있어요.
          </p>
        </>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100"
        >
          취소
        </button>
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="cursor-pointer whitespace-nowrap rounded-md bg-foreground-600 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          결석 저장
        </button>
      </div>
    </Modal>
  );
}